import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, ChevronDown } from "lucide-react";

const LOGO = "/brand/aviana-logo-600.png";

const NAV_GROUPS = [
  {
    label: "Retreats",
    children: [
      { to: "/retreats", label: "All Destination Retreats", desc: "Past · present · upcoming" },
      { to: "/retreats/winter-solstice-retreat", label: "Winter Solstice Retreat", desc: "Galena, Illinois · December 18–20, 2026" },
    ],
  },
  {
    label: "Offerings",
    children: [
      { to: "/walk-with-me", label: "All Events & Offerings", desc: "Every gathering, in one place" },
      { to: "/trainings/aerial-yoga", label: "Aerial Yoga Teacher Training", desc: "50 Hour Aerial Yoga Teacher Training · In-person + online" },
      { to: "/courses/restorative-aerial-yoga-teacher-training", label: "Restorative Online Course", desc: "Anywhere, any time · 25hr cert" },
      { to: "/healing", label: "Reiki & Healing Sessions", desc: "1-on-1 + Level I training" },
      { to: "/reiki", label: "Book a Reiki Session", desc: "Private · Semi · Group · Long distance" },
    ],
  },
  {
    label: "About",
    children: [
      { to: "/about", label: "About Holly", desc: "The teacher behind the work" },
      { to: "/aviana", label: "About Aviana", desc: "The archetype + 11 principles" },
    ],
  },
  {
    label: "Resources",
    children: [
      { to: "/journal", label: "Journal", desc: "Reflections + reading" },
      { to: "/resources", label: "Books & Library", desc: "Soaring with the Soul + more" },
      { to: "/journal/do-aerial-yoga-instructors-need-liability-insurance", label: "Aerial Yoga Liability Insurance", desc: "Do instructors need coverage?" },
    ],
  },
  {
    label: "Join the Collective",
    children: [
      { to: "/host-a-training", label: "Host an Aerial Yoga Training", desc: "Bring Aviana to your studio" },
      { to: "/become-an-aerial-yoga-teacher-trainer", label: "Become an Aerial Yoga Trainer", desc: "The Aviana Legacy Path" },
    ],
  },
];

const SINGLE_LINKS = [
  { to: "/contact", label: "Contact" },
];

function Dropdown({ group }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      data-testid={`nav-group-${group.label.toLowerCase()}`}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 text-[12px] tracking-[0.22em] uppercase text-aviana-muted hover:text-aviana-ink transition-colors py-2"
        aria-expanded={open}
      >
        {group.label}
        <ChevronDown size={12} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 z-50">
          <div className="min-w-[300px] glass-cream rounded-2xl border border-aviana-border/80 shadow-[0_24px_60px_-20px_rgba(31,20,36,0.25)] py-3 overflow-hidden">
            {group.children.map((c) => (
              <Link
                key={c.to}
                to={c.to}
                onClick={() => setOpen(false)}
                className="block px-6 py-3 hover:bg-aviana-cream/60 transition-colors group"
                data-testid={`nav-${c.to.slice(1)}`}
              >
                <p className="font-display text-[18px] text-aviana-ink group-hover:text-aviana-plum transition-colors">{c.label}</p>
                <p className="text-[11px] text-aviana-muted tracking-wide mt-0.5">{c.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);

  return (
    <header
      className="fixed top-0 w-full z-50 glass-cream border-b border-aviana-border/60 shadow-[0_2px_20px_-12px_rgba(31,20,36,0.15)]"
      data-testid="navbar"
    >
      <div className="container-aviana flex items-center justify-between h-24">
        <Link to="/" className="flex items-center gap-3 group shrink-0" data-testid="logo-link">
          <img src={LOGO} alt="Aviana" className="h-12 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_GROUPS.map((g) => <Dropdown key={g.label} group={g} />)}
          {SINGLE_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.slice(1)}`}
              className={({ isActive }) =>
                `text-[12px] tracking-[0.22em] uppercase transition-colors py-2 ${isActive ? "text-aviana-ink" : "text-aviana-muted hover:text-aviana-ink"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4 shrink-0">
          {user && user !== false ? (
            <>
              {user.role === "admin" && (
                <Link to="/admin" className="text-[12px] tracking-[0.22em] uppercase text-aviana-gold hover:text-aviana-goldDeep" data-testid="nav-admin">Admin</Link>
              )}
              <Link to="/dashboard" className="text-[12px] tracking-[0.22em] uppercase text-aviana-muted hover:text-aviana-ink" data-testid="nav-dashboard">
                {user.name?.split(" ")[0] || "Dashboard"}
              </Link>
              <button onClick={logout} className="btn-secondary !py-2.5 !px-5 !text-[11px]" data-testid="logout-btn">Sign out</button>
            </>
          ) : (
            <>
              <Link to="/login" className="text-[12px] tracking-[0.22em] uppercase text-aviana-muted hover:text-aviana-ink" data-testid="login-link">Sign in</Link>
              <Link to="/signup" className="btn-gold !py-2.5 !px-6 !text-[11px]" data-testid="signup-cta">Join</Link>
            </>
          )}
        </div>

        <button className="lg:hidden p-2 text-aviana-ink" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-aviana-border bg-aviana-cream max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
          <div className="container-aviana py-5 flex flex-col gap-1">
            {NAV_GROUPS.map((g) => (
              <div key={g.label} className="border-b border-aviana-border/60">
                <button
                  onClick={() => setExpanded(expanded === g.label ? null : g.label)}
                  className="w-full flex items-center justify-between py-4 text-left text-aviana-ink text-sm uppercase tracking-[0.2em] font-medium"
                >
                  {g.label}
                  <ChevronDown size={14} className={`transition-transform ${expanded === g.label ? "rotate-180" : ""}`} />
                </button>
                {expanded === g.label && (
                  <div className="pb-3 pl-2 space-y-2">
                    {g.children.map((c) => (
                      <Link key={c.to} to={c.to} onClick={() => setOpen(false)} className="block text-aviana-muted text-sm py-2">
                        {c.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {SINGLE_LINKS.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="border-b border-aviana-border/60 py-4 text-aviana-ink text-sm uppercase tracking-[0.2em] font-medium">
                {l.label}
              </Link>
            ))}
            <div className="flex flex-col gap-3 mt-4">
              {user && user !== false ? (
                <>
                  {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="text-aviana-gold py-2 text-sm uppercase tracking-[0.2em]">Admin</Link>}
                  <Link to="/dashboard" onClick={() => setOpen(false)} className="text-aviana-muted py-2 text-sm uppercase tracking-[0.2em]">Dashboard</Link>
                  <button onClick={() => { logout(); setOpen(false); }} className="btn-secondary self-start">Sign out</button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="text-aviana-muted py-2 text-sm uppercase tracking-[0.2em]">Sign in</Link>
                  <Link to="/signup" onClick={() => setOpen(false)} className="btn-gold self-start">Join</Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
